/**
 * Sample the dominant color from an image URL using a small canvas.
 *
 * Algorithm:
 *   1. Load image with CORS so we can read pixel data
 *   2. Draw at low resolution (24×24) into a hidden canvas — fast + denoises
 *   3. Walk every pixel, skip transparent + near-grey/near-white/near-black
 *      bins, then quantize to a 32-step colour cube and tally
 *   4. Return the most-common bin as an `rgb(r,g,b)` string (and the bumped
 *      "vivid" version for an accent — push saturation up so subtle album
 *      art doesn't produce muddy accents)
 *
 * Resolves to null if the image can't be sampled (CORS, network, or no
 * meaningful colours found).
 */

const SAMPLE = 24;
const BIN_SHIFT = 5; // quantize each channel to 32 buckets

const isGrey = (r, g, b) => {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  return max - min < 18;
};

const tooDark = (r, g, b) => r + g + b < 80;
const tooLight = (r, g, b) => r > 240 && g > 240 && b > 240;

const bumpVivid = (r, g, b) => {
  // Push toward saturated by stretching the gap between max & min channels.
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  if (max - min < 30) return { r, g, b };
  const stretch = (v) => {
    const t = (v - min) / Math.max(1, max - min);
    return Math.round(Math.max(0, Math.min(255, t * 255)));
  };
  // Blend 60% vivid + 40% original to keep some warmth.
  const blend = (orig, vivid) => Math.round(orig * 0.4 + vivid * 0.6);
  return {
    r: blend(r, stretch(r)),
    g: blend(g, stretch(g)),
    b: blend(b, stretch(b)),
  };
};

export const getDominantColor = (imageUrl) =>
  new Promise((resolve) => {
    if (!imageUrl) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = SAMPLE;
        canvas.height = SAMPLE;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, SAMPLE, SAMPLE);
        const { data } = ctx.getImageData(0, 0, SAMPLE, SAMPLE);
        const counts = new Map();
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const a = data[i + 3];
          if (a < 200) continue;
          if (isGrey(r, g, b)) continue;
          if (tooDark(r, g, b)) continue;
          if (tooLight(r, g, b)) continue;
          const key = (r >> BIN_SHIFT) << 10 | (g >> BIN_SHIFT) << 5 | (b >> BIN_SHIFT);
          const prev = counts.get(key) || { count: 0, r: 0, g: 0, b: 0 };
          counts.set(key, { count: prev.count + 1, r: prev.r + r, g: prev.g + g, b: prev.b + b });
        }
        if (counts.size === 0) return resolve(null);
        let best = null;
        for (const bin of counts.values()) {
          if (!best || bin.count > best.count) best = bin;
        }
        const r = Math.round(best.r / best.count);
        const g = Math.round(best.g / best.count);
        const b = Math.round(best.b / best.count);
        resolve(bumpVivid(r, g, b));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = imageUrl;
  });

/** Convert an rgb object into the inline style object the music player
 *  spreads onto its wrapper, so the CSS variables stay scoped to that
 *  subtree (and never leak to the rest of the app). */
export const accentStyleVars = (rgb) => {
  if (!rgb) return undefined;
  const { r, g, b } = rgb;
  return {
    "--accent-dynamic": `rgb(${r}, ${g}, ${b})`,
    "--accent-dynamic-glow": `rgba(${r}, ${g}, ${b}, 0.35)`,
  };
};
